import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiShield, 
  FiLock, 
  FiDatabase, 
  FiCpu, 
  FiServer, 
  FiGlobe, 
  FiUserCheck, 
  FiFileText, 
  FiCheckCircle, 
  FiClock, 
  FiMail, 
  FiExternalLink, 
  FiSearch, 
  FiArrowRight, 
  FiSliders, 
  FiLayers, 
  FiCompass, 
  FiCheck,
  FiShare2,
  FiAlertTriangle,
  FiHelpCircle
} from 'react-icons/fi';

const PrivacyPolicy = () => {
  const { currentLang, setIsContactModalOpen } = useLanguage();
  const [activeSection, setActiveSection] = useState('controllers');
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);

  // Interactive mockup preview states for the hero banner
  const [analyticsConsent, setAnalyticsConsent] = useState(true);
  const [aiPersonalizationConsent, setAiPersonalizationConsent] = useState(true);
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Clusters definition (mirrors Eventtia's 5 cluster layout)
  const clusters = [
    {
      id: 'cluster-controllers',
      targetId: 'controllers',
      num: '01',
      title: 'Controllers & Law',
      tag: '§ 1 · Entities · Jurisdictions',
      desc: 'Which BookMeIn.Events entity is responsible for your data, and which territorial jurisdiction applies.',
      color: 'from-indigo-500 to-indigo-600',
      badgeBg: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      arrowBg: 'bg-indigo-600 text-white',
    },
    {
      id: 'cluster-data',
      targetId: 'data-collected',
      num: '02',
      title: 'Data & Purposes',
      tag: '§ 2–3 · Data Categories · Legal Bases',
      desc: 'What personal data we collect, why we collect it, and our lawful basis for each processing activity.',
      color: 'from-purple-500 to-purple-600',
      badgeBg: 'bg-purple-50 text-purple-700 border-purple-200',
      arrowBg: 'bg-purple-600 text-white',
    },
    {
      id: 'cluster-sharing',
      targetId: 'access',
      num: '03',
      title: 'Sharing, AI & Hosting',
      tag: '§ 4–7 · Access · AI & MCP · Cloud Hosting · Retention',
      desc: 'Who can access your data, how AI assistants & MCP connectors interact with records, where data is hosted, and retention periods.',
      color: 'from-pink-500 to-rose-600',
      badgeBg: 'bg-pink-50 text-pink-700 border-pink-200',
      arrowBg: 'bg-pink-600 text-white',
    },
    {
      id: 'cluster-children',
      targetId: 'children',
      num: '04',
      title: 'Children & Cookies',
      tag: '§ 8 & 10 · Minors · Cookie Preferences',
      desc: 'How children’s data is protected through organizers, and what functional & analytics cookies the platform utilizes.',
      color: 'from-amber-500 to-orange-600',
      badgeBg: 'bg-amber-50 text-amber-700 border-amber-200',
      arrowBg: 'bg-amber-600 text-white',
    },
    {
      id: 'cluster-rights',
      targetId: 'rights',
      num: '05',
      title: 'Rights, Security & DPO',
      tag: '§ 9, 11–13 · User Rights · Security Controls · Contact',
      desc: 'The comprehensive rights you can exercise, our zero-trust security safeguards, and direct contact with our Data Protection Officer.',
      color: 'from-emerald-500 to-teal-600',
      badgeBg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      arrowBg: 'bg-emerald-600 text-white',
    },
  ];

  // Sections navigation list
  const sections = [
    { id: 'controllers', title: '1. Data Controllers & Applicable Law', cluster: '01' },
    { id: 'data-collected', title: '2. What Personal Data We Collect', cluster: '02' },
    { id: 'purposes', title: '3. Purposes & Legal Bases', cluster: '02' },
    { id: 'access', title: '4. Access & Sharing', cluster: '03' },
    { id: 'ai-assistants', title: '5. AI Assistants & MCP Server', cluster: '03', highlight: true },
    { id: 'hosting', title: '6. Data Hosting & International Transfers', cluster: '03' },
    { id: 'retention', title: '7. Retention Policy', cluster: '03' },
    { id: 'children', title: '8. Children’s Data', cluster: '04' },
    { id: 'rights', title: '9. Your Privacy Rights', cluster: '05' },
    { id: 'cookies', title: '10. Cookies & Tracking Preferences', cluster: '04' },
    { id: 'security', title: '11. Security Commitments & Breach Protocol', cluster: '05' },
    { id: 'contact', title: '12. Contact & Data Protection Officer', cluster: '05' },
    { id: 'last-updated', title: '13. Version & Last Updated', cluster: '05' },
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 240; // Offset for fixed navbar + breathing room

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 110;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
      setActiveSection(id);
    }
  };

  const handleCopySectionLink = (id) => {
    const url = `${window.location.origin}${window.location.pathname}?page=privacy-policy#${id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {});
  };

  const filteredSections = sections.filter((sec) =>
    sec.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-800 antialiased selection:bg-indigo-100 selection:text-indigo-900 font-sans">
      <Navbar />

      {/* ================= 01 HERO SECTION ================= */}
      <header className="relative pt-32 pb-20 md:pb-28 bg-slate-950 overflow-hidden font-sans border-b border-slate-900">
        {/* Subtle glowing ambient orbs */}
        <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[550px] h-[550px] bg-indigo-600/15 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute top-1/2 right-1/4 translate-x-1/3 w-[500px] h-[500px] bg-purple-600/15 rounded-full blur-[130px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column: Hero Copy */}
            <motion.div 
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
            >
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-semibold tracking-wide uppercase mb-6 backdrop-blur-md">
                <SafeIcon icon={FiShield} className="w-3.5 h-3.5 text-indigo-400" />
                Legal & Compliance
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.12] mb-6">
                Confidentiality &{' '}
                <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  data protection.
                </span>
              </h1>

              <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mb-8 font-normal">
                How BookMeIn.Events collects, processes, and protects your personal data — written once, applied across every entity and event application, in strict alignment with GDPR, CCPA, and global data-protection laws.
              </p>

              <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-slate-400 font-medium">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300">
                  <SafeIcon icon={FiClock} className="w-4 h-4 text-indigo-400" />
                  Version 2.4 · Effective 1 September 2026
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300">
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-emerald-400" />
                  GDPR & CCPA Aligned
                </span>
                <button
                  type="button"
                  onClick={() => scrollToSection('privacy-toc')}
                  className="inline-flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 font-semibold transition-colors underline-offset-4 hover:underline ml-1"
                >
                  Explore index <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5" />
                </button>
              </div>
            </motion.div>

            {/* Right Column: Interactive Privacy Controls Mockup */}
            <motion.div 
              className="lg:col-span-5"
              initial={{ opacity: 0, scale: 0.96, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="relative rounded-2xl bg-gradient-to-b from-slate-800/80 to-slate-900/90 border border-slate-700/60 p-1 shadow-2xl backdrop-blur-xl">
                {/* Glow ring */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl blur-lg opacity-70 pointer-events-none"></div>

                <div className="relative bg-slate-950 rounded-xl overflow-hidden text-slate-100">
                  
                  {/* Browser Bar */}
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-900/80 border-b border-slate-800">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80"></span>
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80"></span>
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80"></span>
                    </div>
                    <div className="text-[11px] font-mono tracking-wider text-slate-400 bg-slate-950/70 px-3 py-1 rounded-md border border-slate-800">
                      bookmein.events · privacy-controls
                    </div>
                    <div className="w-8"></div>
                  </div>

                  {/* Mockup Card Body */}
                  <div className="p-5 sm:p-6 space-y-5">
                    
                    {/* Header Accent Band */}
                    <div className="h-1.5 w-36 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>

                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold text-indigo-400">Data Minimization Engine</div>
                      <div className="text-sm font-semibold text-white mt-0.5">Attendee & Organizer Privacy Center</div>
                    </div>

                    {/* Data Categories List */}
                    <div className="space-y-2.5 pt-1">
                      <div className="flex items-center justify-between text-xs p-2.5 rounded-lg bg-slate-900/90 border border-slate-800">
                        <div className="flex items-center gap-2.5">
                          <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                          <span className="text-slate-300 font-medium">Connection & Telemetry</span>
                        </div>
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                          TLS 1.3
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-xs p-2.5 rounded-lg bg-slate-900/90 border border-slate-800">
                        <div className="flex items-center gap-2.5">
                          <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                          <span className="text-slate-300 font-medium">Identity & Registrations</span>
                        </div>
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">
                          Encrypted
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-xs p-2.5 rounded-lg bg-slate-900/90 border border-slate-800">
                        <div className="flex items-center gap-2.5">
                          <span className="w-2 h-2 rounded-full bg-pink-500"></span>
                          <span className="text-slate-300 font-medium">AI & MCP Scopes (Scotty)</span>
                        </div>
                        <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-pink-500/10 text-pink-400 border border-pink-500/20">
                          Token-Bound
                        </span>
                      </div>
                    </div>

                    {/* Interactive Toggles Preview */}
                    <div className="border-t border-slate-800/80 pt-4 space-y-3">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                        Live Preference Controls
                      </div>

                      {/* Toggle 1 */}
                      <div className="flex items-center justify-between text-xs">
                        <div>
                          <div className="text-slate-200 font-medium">Platform Telemetry & Analytics</div>
                          <div className="text-[10px] text-slate-500">Anonymous performance metrics</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setAnalyticsConsent(!analyticsConsent)}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors duration-200 focus:outline-none ${
                            analyticsConsent ? 'bg-gradient-to-r from-indigo-500 to-purple-500 justify-end' : 'bg-slate-700 justify-start'
                          }`}
                          aria-label="Toggle platform telemetry"
                        >
                          <motion.div 
                            layout 
                            className="bg-white w-4 h-4 rounded-full shadow-md"
                          />
                        </button>
                      </div>

                      {/* Toggle 2 */}
                      <div className="flex items-center justify-between text-xs">
                        <div>
                          <div className="text-slate-200 font-medium">AI Agent Assist (Scotty / MCP)</div>
                          <div className="text-[10px] text-slate-500">Contextual guest assistance</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setAiPersonalizationConsent(!aiPersonalizationConsent)}
                          className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors duration-200 focus:outline-none ${
                            aiPersonalizationConsent ? 'bg-gradient-to-r from-purple-500 to-pink-500 justify-end' : 'bg-slate-700 justify-start'
                          }`}
                          aria-label="Toggle AI agent assist"
                        >
                          <motion.div 
                            layout 
                            className="bg-white w-4 h-4 rounded-full shadow-md"
                          />
                        </button>
                      </div>

                      {/* Toggle 3: Necessary */}
                      <div className="flex items-center justify-between text-xs">
                        <div>
                          <div className="text-slate-200 font-medium">Core Security & Session Cookies</div>
                          <div className="text-[10px] text-slate-500">Required for checkout & login</div>
                        </div>
                        <div className="px-2 py-0.5 text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded">
                          Mandatory
                        </div>
                      </div>
                    </div>

                    {/* Shield Seal Bottom Bar */}
                    <div className="pt-3 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-400">
                      <div className="flex items-center gap-1.5 text-emerald-400 font-medium">
                        <SafeIcon icon={FiShield} className="w-4 h-4" />
                        <span>Zero Data Sale · 100% Owner Owned</span>
                      </div>
                      <span className="text-slate-500">ISO/SOC2 Certified</span>
                    </div>

                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </header>

      {/* ================= 02 DOCUMENT INDEX / TOC ================= */}
      <section className="py-20 bg-white border-b border-slate-200/80 font-sans" id="privacy-toc">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-bold uppercase tracking-wider mb-3">
                <SafeIcon icon={FiCompass} className="w-3.5 h-3.5 text-indigo-600" />
                What's Inside
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                The full policy.{' '}
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Cleanly mapped.
                </span>
              </h2>
            </div>
            <p className="text-slate-600 text-sm sm:text-base max-w-md">
              A quick map of the policy before you dive in. Each cluster collects related sections — jump straight to the topic you need.
            </p>
          </div>

          {/* Cluster Cards Container */}
          <div className="bg-slate-50/70 border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm">
            <div className="flex items-center justify-between pb-6 mb-6 border-b border-slate-200">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Document Index &amp; Navigator
              </span>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-200 text-slate-700">
                13 sections · 5 clusters
              </span>
            </div>

            <div className="space-y-4">
              {clusters.map((cluster) => (
                <button
                  key={cluster.id}
                  type="button"
                  onClick={() => scrollToSection(cluster.targetId)}
                  className="w-full text-left group bg-white hover:bg-indigo-50/40 p-5 rounded-2xl border border-slate-200/80 hover:border-indigo-300 transition-all duration-200 shadow-xs hover:shadow-md flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
                >
                  {/* Cluster Number */}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${cluster.color} text-white font-extrabold text-lg flex items-center justify-center shrink-0 shadow-sm group-hover:scale-105 transition-transform`}>
                    {cluster.num}
                  </div>

                  {/* Cluster Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                      <h3 className="text-base sm:text-lg font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">
                        {cluster.title}
                      </h3>
                      <span className="hidden sm:inline-block text-slate-300">·</span>
                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${cluster.badgeBg}`}>
                        {cluster.tag}
                      </span>
                    </div>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      {cluster.desc}
                    </p>
                  </div>

                  {/* Arrow Action */}
                  <div className={`w-9 h-9 rounded-full ${cluster.arrowBg} flex items-center justify-center shrink-0 self-end sm:self-center shadow-xs group-hover:translate-x-1 transition-transform`}>
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                  </div>
                </button>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ================= 03 MAIN LEGAL TEXT WITH STICKY SIDEBAR ================= */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-sans">
        
        {/* Intro Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-8 mb-10 border-b border-slate-200 gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">
              The Full Text
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
              Privacy policy,{' '}
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                in full.
              </span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Version 2.4 · Effective 1 September 2026
            </span>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          
          {/* Sticky Sidebar Navigation (Desktop) */}
          <aside className="lg:w-1/4 shrink-0">
            <div className="sticky top-28 space-y-6">
              
              {/* Search within document */}
              <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm">
                <div className="relative">
                  <SafeIcon icon={FiSearch} className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search sections..."
                    className="w-full pl-9 pr-3 py-2 text-xs font-medium rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                  />
                </div>
              </div>

              {/* Table of Contents List */}
              <div className="bg-white/85 backdrop-blur-md border border-slate-200/80 p-5 rounded-2xl shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                    <SafeIcon icon={FiLayers} className="w-4 h-4 text-indigo-500" />
                    Sections
                  </h3>
                  <span className="text-[11px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                    {sections.length}
                  </span>
                </div>

                <nav className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
                  {filteredSections.map((sec) => (
                    <button
                      key={sec.id}
                      type="button"
                      onClick={() => scrollToSection(sec.id)}
                      className={`w-full text-left px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-200 flex items-center justify-between group ${
                        activeSection === sec.id
                          ? 'bg-indigo-50 text-indigo-700 border-l-2 border-indigo-600 font-bold'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-2 border-transparent'
                      }`}
                    >
                      <span className="truncate">{sec.title}</span>
                      {sec.highlight && (
                        <span className="shrink-0 ml-1.5 px-1.5 py-0.2 rounded text-[9px] font-extrabold bg-purple-100 text-purple-700 uppercase">
                          AI
                        </span>
                      )}
                    </button>
                  ))}
                  {filteredSections.length === 0 && (
                    <div className="p-4 text-center text-xs text-slate-400">
                      No matching sections found.
                    </div>
                  )}
                </nav>
              </div>

              {/* Quick DPO Card */}
              <div className="bg-gradient-to-br from-indigo-900 to-slate-900 text-white p-5 rounded-2xl shadow-sm border border-indigo-800/40">
                <div className="flex items-center gap-2 text-indigo-300 text-xs font-bold mb-2">
                  <SafeIcon icon={FiMail} className="w-4 h-4" />
                  Direct Privacy Contact
                </div>
                <p className="text-xs text-slate-300 mb-3 leading-relaxed">
                  Have a specific GDPR, CCPA, or data deletion request? Reach our dedicated Data Protection Officer.
                </p>
                <a
                  href="mailto:dpo@bookmein.events?subject=Privacy%20Inquiry%20-%20BookMeIn.Events"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 px-3 py-1.5 rounded-lg transition-colors w-full justify-center"
                >
                  Email DPO Team <SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5" />
                </a>
              </div>

            </div>
          </aside>

          {/* Main Document Content */}
          <article className="lg:w-3/4 space-y-12">
            
            {/* Preamble / Introduction Card */}
            <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm leading-relaxed text-slate-700">
              <p className="text-base sm:text-lg leading-relaxed text-slate-800 font-normal">
                We're BMI Software S.A. ("we", "us", or "our"), trading as BookMeIn.Events, a company registered in Athens, GREECE, under BRN 166543403000, and these <a href="https://gettermscdn.com/view/ygh2T/terms-of-service/en-us" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> (our "Terms") form a legally binding agreement between you and us.
                Protecting your personal data and digital privacy is a foundational principle of <strong>BookMeIn.Events</strong> platform. <br/><br/><hr/><br/>
                Below you will find comprehensive, legally binding information regarding how we process, store, transmit, and safeguard personal information collected through our website, event registration tools, ticketing engines, mobile check-in apps, and AI assistant integrations.
              </p>
            </div>

            {/* ================= SECTION 1: CONTROLLERS & LAW ================= */}
            <div id="controllers" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                    <SafeIcon icon={FiGlobe} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    1. Data Controllers &amp; Applicable Law
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySectionLink('controllers')}
                  className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  title="Copy link to section"
                >
                  <SafeIcon icon={copiedId === 'controllers' ? FiCheck : FiShare2} className="w-4 h-4" />
                </button> 
              </div>

              <p className="text-sm sm:text-base text-slate-600 mb-6 leading-relaxed">
                <strong>BookMeIn.Events</strong> operates globally through designated legal entity. 
                The specific entity <a href="https://bmi-software.gr/" target="_blank" rel="noopener noreferrer"><u>BMI Software S.A</u></a> (EL 801934828) is responsible as data controller and the governing legal jurisdiction and contracting relationship:
              </p>

              {/* 4 Cards Grid mirroring Eventtia's entity structure */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                
                {/* Card 2: European Union & International*/}
                <div className="p-5 rounded-2xl bg-purple-50/50 border border-purple-100/80">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-purple-700">
                    Legal Representative
                  </span>
                  <div className="text-lg font-extrabold text-slate-900 mt-1">BMI Software S.A.</div>
                  <div className="text-xs font-semibold text-slate-500 mb-2"> European Union & EEA & International</div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    Subject to the EU General Data Protection Regulation (GDPR) Regulation (EU) 2016/679.
                  </p>
                </div>

              </div>
            </div>

            {/* ================= SECTION 2: DATA COLLECTED ================= */}
            <div id="data-collected" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                    <SafeIcon icon={FiDatabase} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    2. What Personal Data We Collect
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySectionLink('data-collected')}
                  className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  title="Copy link to section"
                >
                  <SafeIcon icon={copiedId === 'data-collected' ? FiCheck : FiShare2} className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4">
                
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                  <div className="font-bold text-slate-900 text-sm mb-1">
                    a. Connection &amp; Device Telemetry
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    IP address, operating system, browser engine and version, client hints, timestamp logs, device resolution, language preferences, and anonymized diagnostic data.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                  <div className="font-bold text-slate-900 text-sm mb-1">
                    b. Identification &amp; Account Data
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Full name, email address, telephone number, job title, company/organization name, account login credentials (cryptographically hashed), and profile preferences.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                  <div className="font-bold text-slate-900 text-sm mb-1">
                    c. Event Registration &amp; Attendee Lifecycle Data
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Ticket purchases, session bookings, badge QR code identifiers, check-in timestamps, custom organizer survey answers, dietary requirements, and feedback scores.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                  <div className="font-bold text-slate-900 text-sm mb-1">
                    d. Children’s Information
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Processed strictly when provided by event organizers for family or educational gatherings. Minors do not create direct accounts on BookMeIn.Events (see Section 8).
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                  <div className="font-bold text-slate-900 text-sm mb-1">
                    e. Payment &amp; Transaction Tokens
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    We partner with PCI-DSS Level 1 certified payment processors (Stripe). We never store raw credit card numbers or security CVVs on our application servers.
                  </p>
                </div>

              </div>
            </div>

            {/* ================= SECTION 3: PURPOSES & LEGAL BASES ================= */}
            <div id="purposes" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-pink-50 text-pink-600 flex items-center justify-center">
                    <SafeIcon icon={FiFileText} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    3. Purposes &amp; Legal Bases
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySectionLink('purposes')}
                  className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  title="Copy link to section"
                >
                  <SafeIcon icon={copiedId === 'purposes' ? FiCheck : FiShare2} className="w-4 h-4" />
                </button>
              </div>

              <p className="text-sm sm:text-base text-slate-600 mb-6 leading-relaxed">
                Under GDPR Article 6 and international privacy laws, every processing activity must have an established lawful basis:
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs sm:text-sm text-slate-700 border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/80 text-slate-900 font-bold">
                      <th className="py-3 px-4">Processing Purpose</th>
                      <th className="py-3 px-4">Description</th>
                      <th className="py-3 px-4">Lawful Legal Basis</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <tr>
                      <td className="py-3.5 px-4 font-semibold text-slate-900">Service Delivery</td>
                      <td className="py-3.5 px-4 text-slate-600">Managing accounts, processing ticket orders, issuing attendee badges, and session check-ins.</td>
                      <td className="py-3.5 px-4"><span className="px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 font-bold text-xs">Contractual Performance</span></td>
                    </tr>
                    <tr>
                      <td className="py-3.5 px-4 font-semibold text-slate-900">Administration &amp; Alerts</td>
                      <td className="py-3.5 px-4 text-slate-600">Sending booking confirmations, event schedule shifts, critical security alerts, and system notices.</td>
                      <td className="py-3.5 px-4"><span className="px-2.5 py-1 rounded-md bg-purple-50 text-purple-700 font-bold text-xs">Contract + Legitimate Interest</span></td>
                    </tr>
                    <tr>
                      <td className="py-3.5 px-4 font-semibold text-slate-900">Platform Optimization</td>
                      <td className="py-3.5 px-4 text-slate-600">Analyzing aggregated traffic, debugging latency, and refining organizer UX and check-in speeds.</td>
                      <td className="py-3.5 px-4"><span className="px-2.5 py-1 rounded-md bg-blue-50 text-blue-700 font-bold text-xs">Legitimate Interest</span></td>
                    </tr>
                    <tr>
                      <td className="py-3.5 px-4 font-semibold text-slate-900">Marketing &amp; Newsletters</td>
                      <td className="py-3.5 px-4 text-slate-600">Feature updates, promotional offers, webinars, and partner benefits (with 1-click opt-out).</td>
                      <td className="py-3.5 px-4"><span className="px-2.5 py-1 rounded-md bg-amber-50 text-amber-700 font-bold text-xs">Explicit Consent</span></td>
                    </tr>
                    <tr>
                      <td className="py-3.5 px-4 font-semibold text-slate-900">Event Organizer Processing</td>
                      <td className="py-3.5 px-4 text-slate-600">Acting as an authorized Data Processor on behalf of organizers who control event databases.</td>
                      <td className="py-3.5 px-4"><span className="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 font-bold text-xs">Data Processor Agreement (DPA)</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* ================= SECTION 4: ACCESS & SHARING ================= */}
            <div id="access" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                    <SafeIcon icon={FiUserCheck} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    4. Access &amp; Sharing
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySectionLink('access')}
                  className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  title="Copy link to section"
                >
                  <SafeIcon icon={copiedId === 'access' ? FiCheck : FiShare2} className="w-4 h-4" />
                </button>
              </div>

              <p className="text-sm sm:text-base text-slate-600 mb-6 leading-relaxed">
                We enforce strict data isolation and the principle of least privilege. We never sell your personal information. Data access is restricted to:
              </p>

              <ul className="space-y-3.5 text-xs sm:text-sm text-slate-700 list-none pl-0">
                <li className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">✓</span>
                  <div>
                    <strong className="text-slate-900">Authorized Personnel:</strong> Internal engineers and support specialists subject to confidentiality agreements, accessed only when resolving verified support tickets.
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">✓</span>
                  <div>
                    <strong className="text-slate-900">Event Organizers:</strong> Organizers whose events you attend act as independent Data Controllers for their attendee manifests.
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">✓</span>
                  <div>
                    <strong className="text-slate-900">Vetted Sub-Processors:</strong> Cloud infrastructure providers (AWS, Supabase), transactional email services, and analytics hosts operating under signed DPAs.
                  </div>
                </li>
                <li className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0 mt-0.5 font-bold text-xs">✓</span>
                  <div>
                    <strong className="text-slate-900">Legal Authorities:</strong> Only when compelled by lawful subpoenas, court orders, or statutory regulatory mandates.
                  </div>
                </li>
              </ul>
            </div>

            {/* ================= SECTION 5: AI ASSISTANTS & MCP SERVER ================= */}
            <div id="ai-assistants" className="scroll-mt-28 bg-gradient-to-br from-indigo-950 via-slate-900 to-purple-950 text-white border border-indigo-800/50 rounded-3xl p-6 sm:p-10 shadow-xl relative overflow-hidden">
              
              {/* Background ambient lighting */}
              <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>

              <div className="relative z-10">
                <div className="flex items-center justify-between pb-4 mb-6 border-b border-indigo-800/60">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 flex items-center justify-center">
                      <SafeIcon icon={FiCpu} className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold text-indigo-400 uppercase tracking-wider">
                        Next-Generation Event Intelligence
                      </div>
                      <h2 className="text-xl sm:text-2xl font-extrabold text-white">
                        5. AI Assistants &amp; MCP Server Integration
                      </h2>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopySectionLink('ai-assistants')}
                    className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                    title="Copy link to section"
                  >
                    <SafeIcon icon={copiedId === 'ai-assistants' ? FiCheck : FiShare2} className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-5 text-xs sm:text-sm text-slate-300 leading-relaxed font-normal">
                  <p>
                    When you connect an AI assistant (such as <strong>Scotty</strong>, Claude, ChatGPT, Cursor, Microsoft Copilot, or custom organizational agents) to BookMeIn.Events through our <strong>Model Context Protocol (MCP) server</strong> or REST APIs, BookMeIn.Events processes the precise data needed to execute the query or action you request.
                  </p>

                  <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
                    <div className="font-bold text-white text-sm flex items-center gap-2">
                      <SafeIcon icon={FiLock} className="w-4 h-4 text-purple-400" />
                      Scope Inheritance &amp; Token Boundary
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      The MCP server sits directly on top of our secure API layer and inherits the exact scopes and granular permissions of the authenticated user token. An AI assistant cannot read, write, or query any event record that the authenticated user does not have permission to access.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div className="p-4 rounded-xl bg-indigo-950/60 border border-indigo-800/40">
                      <div className="text-white font-bold text-xs uppercase tracking-wide mb-1 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                        Standard Permitted Fields
                      </div>
                      <p className="text-xs text-slate-300">
                        Event schedules, session titles, speaker bios, public ticket tiers, aggregate attendance numbers, and authenticated attendee registration confirmations.
                      </p>
                    </div>

                    <div className="p-4 rounded-xl bg-purple-950/60 border border-purple-800/40">
                      <div className="text-white font-bold text-xs uppercase tracking-wide mb-1 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                        Protected Fields (Never Default)
                      </div>
                      <p className="text-xs text-slate-300">
                        Date of birth, government ID numbers, personal phone numbers, and custom confidential fields are blocked from AI extraction unless explicitly authorized.
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-indigo-800/60 pt-4 space-y-2">
                    <div className="text-white font-bold text-sm">
                      Zero Foundation Model Training Commitment
                    </div>
                    <p className="text-xs text-slate-300">
                      We <strong>never</strong> permit third-party LLM providers (including OpenAI or Anthropic) to utilize your attendee records, organizer confidential data, or ticket transactions to train, fine-tune, or improve public AI models. Data transmission through AI connectors is strictly zero-retention for model training.
                    </p>
                  </div>

                  <div className="border-t border-indigo-800/60 pt-4 text-xs text-slate-400 flex flex-wrap items-center justify-between gap-3">
                    <span>Instant Revocation: Disconnect any MCP connector or revoke API keys anytime in Dashboard Settings.</span>
                    <span className="text-amber-300 font-medium">Prohibited: Health data, biometric identifiers, and payment card CVVs.</span>
                  </div>
                </div>

              </div>
            </div>

            {/* ================= SECTION 6: HOSTING & TRANSFERS ================= */}
            <div id="hosting" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
                    <SafeIcon icon={FiServer} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    6. Data Hosting &amp; International Transfers
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySectionLink('hosting')}
                  className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  title="Copy link to section"
                >
                  <SafeIcon icon={copiedId === 'hosting' ? FiCheck : FiShare2} className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                
                <div className="p-5 rounded-2xl bg-indigo-50/60 border border-indigo-100">
                  <span className="text-xs font-bold uppercase tracking-wider text-indigo-700">
                    European Union Users
                  </span>
                  <div className="text-lg font-extrabold text-slate-900 mt-1 mb-2">
                    Hosted in the European Union
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Personal data of users residing in the EU/EEA is hosted in Frankfurt, Germany and Dublin, Ireland, protected under GDPR without cross-border transfers.
                  </p>
                </div>

                <div className="p-5 rounded-2xl bg-pink-50/60 border border-pink-100">
                  <span className="text-xs font-bold uppercase tracking-wider text-pink-700">
                    Users Outside the EU
                  </span>
                  <div className="text-lg font-extrabold text-slate-900 mt-1 mb-2">
                    Hosted in Singapore
                  </div>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                    Personal data of users located in the Asia Pacific region, Oceania, and internationally is hosted in SOC 2 Type II certified data centers with multi-zone redundancy.
                  </p>
                </div>

              </div>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 text-xs sm:text-sm text-slate-700 space-y-2">
                <div className="font-bold text-slate-900">Standard Contractual Clauses (SCCs)</div>
                <p className="leading-relaxed">
                  All cross-border transfers outside of equivalent jurisdictions are safeguarded by current European Commission Standard Contractual Clauses (SCCs) and UK Addenda. A complete list of active sub-processors is available upon request at <a href="mailto:dpo@bookmein.events" className="text-indigo-600 underline font-semibold">dpo@bookmein.events</a>.
                </p>
              </div>
            </div>

            {/* ================= SECTION 7: RETENTION ================= */}
            <div id="retention" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-pink-50 text-pink-600 flex items-center justify-center">
                    <SafeIcon icon={FiClock} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    7. Retention Policy
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySectionLink('retention')}
                  className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  title="Copy link to section"
                >
                  <SafeIcon icon={copiedId === 'retention' ? FiCheck : FiShare2} className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                  <div className="font-bold text-slate-900 mb-1">Account Profiles</div>
                  <p className="text-slate-600 leading-relaxed">
                    Maintained for the duration of your active account. Accounts dormant for 36 months are flagged for automated secure purging.
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                  <div className="font-bold text-slate-900 mb-1">Event Data &amp; Tickets</div>
                  <p className="text-slate-600 leading-relaxed">
                    Retained as directed by event organizers (controllers), typically up to 7 years for tax, audit, and legal accounting obligations.
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                  <div className="font-bold text-slate-900 mb-1">Cookies &amp; Telemetry</div>
                  <p className="text-slate-600 leading-relaxed">
                    Kept between 13 to 25 months depending on type, after which session identifiers automatically expire.
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                  <div className="font-bold text-slate-900 mb-1">Cryptographic Shredding</div>
                  <p className="text-slate-600 leading-relaxed">
                    When data retention schedules lapse or deletion is requested, records are irrecoverably sanitized using DoD-compliant cryptographic wiping.
                  </p>
                </div>
              </div>
            </div>

            {/* ================= SECTION 8: CHILDREN'S DATA ================= */}
            <div id="children" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                    <SafeIcon icon={FiAlertTriangle} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    8. Children’s Privacy
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySectionLink('children')}
                  className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  title="Copy link to section"
                >
                  <SafeIcon icon={copiedId === 'children' ? FiCheck : FiShare2} className="w-4 h-4" />
                </button>
              </div>

              <div className="p-5 rounded-2xl bg-amber-50/50 border border-amber-200/70 text-xs sm:text-sm text-slate-700 space-y-3">
                <p className="leading-relaxed">
                  BookMeIn.Events does not collect personal data from its users under the age of 16 without parental or legal guardian's authorization.
                </p>
                <ul className="space-y-2 list-disc pl-5 text-slate-600">
                  <li>Children under the age of 16 without parental or legal guardian's authorization cannot directly create an organizer or attendee account on BookMeIn.Events.</li>
                  <li>When an organizer collects registration data for family or youth gatherings, the event organizer (controller) is strictly responsible for obtaining verifiable parental consent.</li>
                  <li>BookMeIn.Events acts solely as a technical data processor in such circumstances. If we discover unauthorized collection of minor data, we purge it immediately.</li>
                </ul>
              </div>
            </div>

            {/* ================= SECTION 9: YOUR RIGHTS ================= */}
            <div id="rights" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <SafeIcon icon={FiUserCheck} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    9. Your Privacy Rights
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySectionLink('rights')}
                  className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  title="Copy link to section"
                >
                  <SafeIcon icon={copiedId === 'rights' ? FiCheck : FiShare2} className="w-4 h-4" />
                </button>
              </div>

              <p className="text-sm sm:text-base text-slate-600 mb-6 leading-relaxed">
                Regardless of your geographic location, BookMeIn.Events extends enterprise-grade data subject rights:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                {[
                  { title: 'Right to Access', desc: 'Request a machine-readable copy of all personal records we hold about you.' },
                  { title: 'Right to Rectification', desc: 'Correct inaccurate or outdated biographical and account details.' },
                  { title: 'Right to Erasure', desc: 'Request complete deletion ("Right to be Forgotten") of your account and logs.' },
                  { title: 'Right to Restriction', desc: 'Limit how your records are processed during pending accuracy disputes.' },
                  { title: 'Data Portability', desc: 'Export your event data in standard JSON or CSV formats.' },
                  { title: 'Right to Object', desc: 'Opt out of marketing communications or algorithmic profiling instantly.' },
                ].map((right, idx) => (
                  <div key={idx} className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                    <div className="font-bold text-slate-900 text-sm mb-1">{right.title}</div>
                    <p className="text-xs text-slate-600 leading-relaxed">{right.desc}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between p-4 rounded-2xl bg-indigo-50 border border-indigo-100 gap-4">
                <div className="text-xs text-indigo-900 font-medium">
                  To exercise any of these rights, email our compliance team. We respond within 30 days without cost.
                </div>
                <a
                  href="mailto:dpo@bookmein.events?subject=Data%20Subject%20Request"
                  className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition-colors shrink-0 shadow-xs"
                >
                  Submit Data Request →
                </a>
              </div>
            </div>

            {/* ================= SECTION 10: COOKIES ================= */}
            <div id="cookies" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                    <SafeIcon icon={FiSliders} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    10. Cookies &amp; Tracking Preferences
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySectionLink('cookies')}
                  className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  title="Copy link to section"
                >
                  <SafeIcon icon={copiedId === 'cookies' ? FiCheck : FiShare2} className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4 text-xs sm:text-sm text-slate-600 leading-relaxed">
                <p>
                  We utilize cookies and local storage tokens to deliver a secure, fast, and personalized event experience:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                    <div className="font-bold text-slate-900 mb-1">Strictly Necessary</div>
                    <p className="text-xs text-slate-600">Essential for authentication, secure checkout, CSRF protection, and ticket cart persistence.</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                    <div className="font-bold text-slate-900 mb-1">Analytics &amp; Speed</div>
                    <p className="text-xs text-slate-600">Aggregated usage statistics to optimize page builder speeds and ticket buying flows.</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                    <div className="font-bold text-slate-900 mb-1">Functional &amp; Chat</div>
                    <p className="text-xs text-slate-600">Remembers selected language, currency, and AI chatbot session context.</p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 space-y-2 pt-3">
                  <div className="font-bold text-slate-900">How to Deactivate Cookies</div>
                  <p className="text-xs text-slate-600">
                    You can manage or disable non-essential cookies via your web browser settings. To opt out of Google Analytics across all sites, you can install the official browser add-on at{' '}
                    <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline font-semibold">
                      https://tools.google.com/dlpage/gaoptout
                    </a>. Detailed instructions on cookie management across Chrome, Safari, and Firefox can be reviewed at{' '}
                    <a href="https://allaboutcookies.org/how-to-manage-cookies" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline font-semibold">
                      allaboutcookies.org
                    </a>.
                  </p>
                </div>
              </div>
            </div>

            {/* ================= SECTION 11: SECURITY ================= */}
            <div id="security" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                    <SafeIcon icon={FiShield} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    11. Security Commitments &amp; Incident Protocols
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySectionLink('security')}
                  className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  title="Copy link to section"
                >
                  <SafeIcon icon={copiedId === 'security' ? FiCheck : FiShare2} className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4 text-xs sm:text-sm text-slate-700 leading-relaxed">
                <p>
                  We implement multi-layered physical, organizational, and technical security controls to safeguard data integrity:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                    <div className="font-bold text-slate-900 mb-1">State-of-the-Art Encryption</div>
                    <p className="text-xs text-slate-600">TLS 1.3 encryption for all traffic in-transit and AES-256 bit encryption for all database volumes at rest.</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                    <div className="font-bold text-slate-900 mb-1">Continuous Monitoring</div>
                    <p className="text-xs text-slate-600">24/7 automated intrusion detection, automated dependency audits, and regular third-party penetration testing.</p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                  <div className="font-bold text-slate-900 mb-1">72-Hour Breach Notification Commitment</div>
                  <p className="text-xs text-slate-600">
                    In the improbable event of a verified data breach impacting personal records, we notify relevant supervisory authorities and affected organizers within 72 hours, supplying remedial action reports.
                  </p>
                </div>
              </div>
            </div>

            {/* ================= SECTION 12: CONTACT & DPO ================= */}
            <div id="contact" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                    <SafeIcon icon={FiMail} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    12. Contact &amp; Data Protection Officer
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopySectionLink('contact')}
                  className="text-slate-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-slate-50 transition-colors"
                  title="Copy link to section"
                >
                  <SafeIcon icon={copiedId === 'contact' ? FiCheck : FiShare2} className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm">
                <div className="p-5 rounded-2xl bg-indigo-50/60 border border-indigo-100">
                  <div className="font-bold text-slate-900 text-base mb-1">Data Protection Officer (DPO)</div>
                  <div className="text-slate-600 mb-3">For formal GDPR, CCPA, and compliance inquiries:</div>
                  <a href="mailto:dpo@bookmein.events" className="inline-flex items-center gap-2 font-bold text-indigo-600 hover:text-indigo-700">
                    <SafeIcon icon={FiMail} className="w-4 h-4" />
                    dpo@bookmein.events
                  </a>
                </div>

                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200">
                  <div className="font-bold text-slate-900 text-base mb-1">General Privacy Support</div>
                  <div className="text-slate-600 mb-3">For event account questions and attendee assistance:</div>
                  <a href="mailto:help@bookmein.events" className="inline-flex items-center gap-2 font-bold text-indigo-600 hover:text-indigo-700">
                    <SafeIcon icon={FiHelpCircle} className="w-4 h-4" />
                    support@bookmein.events
                  </a>
                </div>
              </div>
            </div>

            {/* ================= SECTION 13: LAST UPDATED ================= */}
            <div id="last-updated" className="scroll-mt-28 bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-sm">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                    <SafeIcon icon={FiClock} className="w-4.5 h-4.5" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    13. Version &amp; Last Updated
                  </h2>
                </div>
              </div>

              <div className="text-xs sm:text-sm text-slate-600 leading-relaxed space-y-2">
                <p>
                  <strong>Effective Date:</strong> September 1st, 2026 · Version 2.4
                </p>
                <p>
                  We periodically update this policy to reflect new product modules, cloud region additions, or evolving statutory requirements. When material changes occur, we alert active account owners via email and in-dashboard notifications prior to their effective date.
                </p>
              </div>
            </div>

          </article>
        </div>

      </section>

      {/* ================= 04 ENTERPRISE DPA CALLOUT BANNER ================= */}
      <section className="py-16 bg-slate-950 text-white font-sans border-t border-slate-900 relative overflow-hidden">
        {/* Glow orb */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-indigo-600/15 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-gradient-to-r from-slate-900 via-indigo-950/60 to-slate-900 border border-slate-800 rounded-3xl p-8 sm:p-12 shadow-2xl flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-xs font-bold uppercase tracking-wider mb-4">
                <SafeIcon icon={FiShield} className="w-3.5 h-3.5" />
                If We Process Data On Your Behalf
              </div>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-3">
                The Data Processor Agreement details{' '}
                <span className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  how that works.
                </span>
              </h3>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                When BookMeIn.Events acts as a processor for personal data you control as an event organizer, our standard Data Processor Agreement (DPA) governs the relationship — including documented instructions, audit rights, sub-processor notifications, and security commitments.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setIsContactModalOpen(true)}
                className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold text-sm transition-all duration-200 shadow-lg shadow-indigo-600/25 flex items-center justify-center gap-2 group cursor-pointer"
              >
                Request Enterprise DPA
                <SafeIcon icon={FiArrowRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
